# Wellness Frontier Consumer Health Data Privacy Policy

**Effective date:** September 1, 2026  
**Last updated:** August 31, 2026

This policy describes how Wellness Frontier, operated by Advanced Health and
Wellness Group, collects, uses, and shares consumer health data as those terms
are defined by the Washington My Health My Data Act.

## 1. Categories of consumer health data we collect and why

| Category | Examples | Purpose |
|---|---|---|
| Bodily functions, vital signs, and measurements | steps, sleep duration, resting heart rate | show your measurements and progress, support goals, and operate the wellness features you request |
| Health and wellness responses | answers you choose to provide in a health or wellness survey | provide your wellness experience and create employer reports that contain participation counts, not your answers |
| Wellness interests and choices | the wellness area you voluntarily select as most important for the month | personalize the program and create company-wide monthly totals |
| Wellness-program activity | completed actions, connected-source status, goals, points, achievements, and days-in-a-row progress | operate the program, recognize participation, and create the limited employer reports described in our general Privacy Policy |
| Benefits and care-seeking questions | a question you submit to an enabled benefits coach | answer the question from employer plan documents and improve the coach using a de-identified question |
| Information that identifies health-service use | use of health-related app features | provide the feature, protect the service, and meet legal obligations |

We also process identifiers, such as your name, work email, and account
reference, when needed to associate consumer health data with your account.

## 2. Sources of consumer health data

We collect consumer health data from:

- you, when you answer a question, set a goal, choose a wellness area, or use a
  program feature;
- Apple Health or Health Connect, after you approve specific read access;
- program records created when you complete an action, earn points, or use a
  feature; and
- your employer, for account and workplace information used to make the
  program available to you.

## 3. Categories of consumer health data we share

We share the following categories only as needed to provide the service you
request or as otherwise permitted by law:

- consumer health data processed by our contracted service providers for
  hosting, databases, storage, email delivery, security, support, document
  processing, search, and the AI coach;
- named program-participation information provided to your employer, including
  your name, points, and place on the points board if you have points;
- participation counts and group-level wellness-program totals provided to
  your employer; and
- consumer health data required by valid legal process.

We do not share your individual connected-health measurements, survey answers,
coach or benefits questions, medical history, or sensitive-support-resource
use with your employer. We do not sell consumer health data.

## 4. Third parties and affiliates

The categories of third parties with which we share consumer health data are:

- **Microsoft Corporation and its Azure services**, as contracted processors
  for cloud hosting, databases, storage, email delivery, security, document
  processing, search, and Azure OpenAI when the coach is enabled;
- **your employer**, only for the employer-visible program information and
  group-level reports described above; and
- **government authorities or other recipients required by law**, when a valid
  legal requirement applies.

**Specific affiliates:** none.

## 5. How to exercise your rights

Subject to applicable law, you may:

- confirm whether we collect, share, or sell consumer health data about you;
- access your consumer health data and receive a list of third parties and
  affiliates with which it was shared or sold, including a way to contact them;
- withdraw consent to future collection or sharing; and
- request deletion of consumer health data, including notice of the request to
  processors and other recipients as required by law.

You may use the app’s Account area to disconnect a health source or request
account deletion where that control is available. You may exercise any right,
or appeal a refusal to act, by emailing **privacy@wellnessfrontier.ai**. You do
not need to create a new account to make a request. We may ask for information
reasonably needed to authenticate you and the request.

We will respond without undue delay and within 45 days. When reasonably
necessary, we may extend the response period once by up to 45 additional days
and will tell you why during the first 45 days. Responses are free up to twice
per year unless a request is manifestly unfounded, excessive, or repetitive.

If we deny an appeal, we will explain the decision in writing and provide a way
to contact the Washington State Attorney General.

